import { useParams } from "react-router";
import { styled } from "styled-components";

import { bookmarksApiSlice } from "api/bookmarksApi.slice";
import { UIActionButton, UIButtonsRow } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { KnowledgeItemPinButton } from "domains/pinAddReject/components/KnowledgeItemPinButton";
import { KnowledgeRejectButton } from "domains/pinAddReject/components/KnowledgeRejectButton";
import { shareLinkAction } from "domains/share/actions";
import type { KnowledgeType } from "entities/knowledge/types";
import { getCanDisplayBookmarks } from "entities/permissions/selectors";

const StyledButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px 1rem;
  margin-top: 5px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

type BaseProps = {
  id: string;
  title: string;
  link: string;
  shareLink: string;
};

type KnowledgeProps = BaseProps & {
  variant: "knowledge";
  knowledgeType: KnowledgeType;
  isBookmarked: boolean;
  isPinned: boolean;
};

type CaseStudyProps = BaseProps & {
  variant: "caseStudy";
  knowledgeType?: never;
  isBookmarked?: never;
  isPinned?: never;
};

type KnowledgeButtonsProps = {
  className?: string;
  fullwidth?: boolean;
} & (KnowledgeProps | CaseStudyProps);

export const KnowledgeButtons = ({
  id,
  title,
  knowledgeType,
  link,
  isBookmarked,
  isPinned,
  shareLink,
  fullwidth,
  className,
  variant,
}: KnowledgeButtonsProps) => {
  const dispatch = useDispatch();
  const { topicId } = useParams();
  const accessToBookmarks = useSelector(getCanDisplayBookmarks);

  const track = useTrack();

  const [addKnowledgeToBookmarks] = bookmarksApiSlice.endpoints.addKnowledgeToBookmarks.useMutation();
  const [removeKnowledgeFromBookmarks] = bookmarksApiSlice.endpoints.removeKnowledgeFromBookmarks.useMutation();

  const onBookmarkHandle = async () => {
    if (isBookmarked) {
      await removeKnowledgeFromBookmarks({ id });
    } else {
      await addKnowledgeToBookmarks({ id });
    }
  };

  const trackOpenLink = () => track({ entityId: id, entityName: title, entityType: "Knowledge", name: "Open Link" });

  let completeShareLink = `${import.meta.env.VITE_WEBSITE_URL}${shareLink}`;
  if (shareLink && !shareLink.startsWith("/")) {
    completeShareLink = shareLink;
  }

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id: id,
        name: title,
        link: completeShareLink,
        type: "Knowledge",
      }),
    );

  return fullwidth ? (
    <StyledButtonContainer>
      <StyledButtonRow className={className}>
        <UIActionButton type="open" href={link} onClick={trackOpenLink} />
        <UIActionButton type="share" onClick={onShareClick} />
        {accessToBookmarks && variant === "knowledge" && (
          <UIActionButton type="bookmark" isActive={isBookmarked} onClick={onBookmarkHandle} />
        )}
      </StyledButtonRow>
      <StyledButtonRow className={className}>
        {topicId && variant === "knowledge" && (
          <>
            <KnowledgeRejectButton knowledgeId={id} knowledgeType={knowledgeType} topicId={topicId} hideButtonLabels />
            <KnowledgeItemPinButton topicId={topicId} knowledgeId={id} hideButtonLabels isPinned={isPinned} />
          </>
        )}
      </StyledButtonRow>
    </StyledButtonContainer>
  ) : (
    <StyledButtonContainer>
      <UIButtonsRow className={className}>
        <UIActionButton type="open" href={link} onClick={trackOpenLink} />
        <UIActionButton type="share" onClick={onShareClick} />
        {accessToBookmarks && variant === "knowledge" && (
          <UIActionButton type="bookmark" isActive={isBookmarked} onClick={onBookmarkHandle} />
        )}
      </UIButtonsRow>
    </StyledButtonContainer>
  );
};
